'use client'

import { useRef, useEffect } from 'react'
import { usePathname } from 'next/navigation'

// Types
import type { ScrollRestoreProviderProps } from '@/types/components/providers/scroll-restore'

// Helpers
import { useScrollStore } from '@/helpers/store/scroll'

const ScrollRestoreProvider = (props: ScrollRestoreProviderProps) => {
	// Props
	const { children } = props

	// Variables
	const pathname = usePathname()
	const { save, restore } = useScrollStore()
	const cancelRetryRef = useRef<boolean>(false)
	const userScrolledRef = useRef<boolean>(false)

	// Callbacks
	useEffect(() => {
		cancelRetryRef.current = false
		userScrolledRef.current = false

		const saved = restore(pathname)

		const handleClick = (e: MouseEvent) => {
			const anchor = (e.target as HTMLElement).closest('a')

			if (anchor?.href) {
				const url = new URL(anchor.href)

				if (url.origin === window.location.origin) save(window.location.pathname)
			}
		}

		const handleUserScroll = () => {
			userScrolledRef.current = true
		}

		document.addEventListener('click', handleClick, true)

		window.addEventListener('wheel', handleUserScroll, { passive: true })
		window.addEventListener('touchmove', handleUserScroll, { passive: true })

		let frameId = 0
		let attempts = 0
		const maxAttempts = 60

		if (saved !== undefined && saved > 0) {
			const tryRestore = () => {
				if (cancelRetryRef.current || userScrolledRef.current) return

				const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)

				if (maxScroll >= saved || Math.abs(window.scrollY - saved) < 2) {
					if (Math.abs(window.scrollY - saved) >= 2) {
						window.scrollTo({ top: saved, left: 0, behavior: 'instant' })
					}

					return
				}

				window.scrollTo({ top: Math.min(saved, maxScroll), left: 0, behavior: 'instant' })
				attempts += 1

				if (attempts < maxAttempts) frameId = window.requestAnimationFrame(tryRestore)
			}

			frameId = window.requestAnimationFrame(tryRestore)
		}

		return () => {
			cancelRetryRef.current = true
			document.removeEventListener('click', handleClick, true)
			window.removeEventListener('wheel', handleUserScroll)
			window.removeEventListener('touchmove', handleUserScroll)
			if (frameId) window.cancelAnimationFrame(frameId)
		}
	}, [pathname, save, restore])

	return <>{children}</>
}

export default ScrollRestoreProvider
